import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Linking,
  Platform,
} from 'react-native';
import { BlurView } from 'expo-blur';
import { CircleAlert as AlertCircle } from 'lucide-react-native';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('ForceUpdateModal');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
import { useTranslation } from '@/hooks/useTranslation';
import { palette, overlay, spacing, radius, typography } from '@/styles/tokens';

interface ForceUpdateModalProps {
  visible: boolean;
  storeUrl: string;
  currentVersion: string;
  minimumVersion: string;
}

export function ForceUpdateModal({
  visible,
  storeUrl,
  currentVersion,
  minimumVersion,
}: ForceUpdateModalProps) {
  const { t } = useTranslation();
  const handleUpdatePress = async () => {
    logWithTimestamp(`🔄 [FORCE-UPDATE] User clicked update button`);
    logWithTimestamp(`🔄 [FORCE-UPDATE] Current version: ${currentVersion}, Required: ${minimumVersion}`);

    try {
      const canOpen = await Linking.canOpenURL(storeUrl);

      if (!canOpen) {
        errorWithTimestamp('🔄 [FORCE-UPDATE] Cannot open store URL:', storeUrl);
        return;
      }

      await Linking.openURL(storeUrl);
      logWithTimestamp(`🔄 [FORCE-UPDATE] Opened store: ${storeUrl}`);
    } catch (error) {
      errorWithTimestamp('🔄 [FORCE-UPDATE] Error opening store:', error);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      hardwareAccelerated
    >
      <BlurView intensity={80} style={styles.blurContainer}>
        <View style={styles.overlay}>
          <View style={styles.modalContainer}>
            <View style={styles.iconContainer}>
              <AlertCircle size={64} color={palette.semantic.error} strokeWidth={2} />
            </View>

            <Text style={styles.title}>{t('updateRequired')}</Text>

            <Text style={styles.message}>
              {t('forceUpdateDefaultMessage')}
            </Text>

            <View style={styles.versionInfo}>
              <Text style={styles.versionLabel}>{t('currentVersion')}:</Text>
              <Text style={styles.versionValue}>{currentVersion}</Text>
            </View>

            <View style={styles.versionInfo}>
              <Text style={styles.versionLabel}>{t('requiredVersion')}:</Text>
              <Text style={styles.versionValue}>{minimumVersion}</Text>
            </View>

            <TouchableOpacity
              style={styles.updateButton}
              onPress={handleUpdatePress}
              activeOpacity={0.8}
            >
              <Text style={styles.updateButtonText}>{t('updateNow')}</Text>
            </TouchableOpacity>

            <Text style={styles.footerText}>
              {t('appCannotBeUsedWithoutUpdate')}
            </Text>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  blurContainer: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: overlay.scrimMedium,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  modalContainer: {
    backgroundColor: palette.neutral[700],
    borderRadius: radius.xxl,
    padding: spacing.xxl,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: palette.black,
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.44,
    shadowRadius: 10.32,
    elevation: 16,
  },
  iconContainer: {
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.weight.bold,
    color: palette.white,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  message: {
    fontSize: typography.fontSize.base,
    color: palette.neutral[100],
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xl,
  },
  versionInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    backgroundColor: palette.neutral[600],
    borderRadius: radius.md,
    marginBottom: spacing.xs,
  },
  versionLabel: {
    fontSize: typography.fontSize.md,
    color: palette.neutral[300],
  },
  versionValue: {
    fontSize: typography.fontSize.md,
    color: palette.white,
    fontWeight: typography.weight.semibold,
  },
  updateButton: {
    backgroundColor: palette.semantic.error,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xxl,
    borderRadius: radius.lg,
    width: '100%',
    alignItems: 'center',
    marginTop: spacing.xl,
    shadowColor: palette.semantic.error,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  updateButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.bold,
    color: palette.white,
  },
  footerText: {
    fontSize: typography.fontSize.sm,
    color: palette.neutral[350],
    textAlign: 'center',
    marginTop: spacing.md,
    fontStyle: 'italic',
  },
});
